import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { translateStatus, translateUrgency, translatePartnerType } from '../i18n/translations';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, ArrowRight, Package, MapPin, Archive, ChevronDown, ChevronRight, ImageIcon, Globe, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { StatusFlowBar } from '../components/StatusFlowBar';

const LOGO_URL = "https://www.mypanther.fr/web/image/website/1/logo/My%20Panther?unique=501ee63";
const LANG_LABELS = { fr: "Français", en: "English", vi: "Tiếng Việt" };

const getRowColor = (order, partnerType) => {
  const now = new Date();
  if (partnerType === 'couture') {
    const bureauDate = order.bureau_delivery_date ? new Date(order.bureau_delivery_date) : null;
    if (!bureauDate) return '';
    const diffDays = Math.floor((now - bureauDate) / (1000 * 60 * 60 * 24));
    if (diffDays > 3) return 'bg-red-50 border-l-4 border-l-red-500';
    if (diffDays >= 1) return 'bg-amber-50 border-l-4 border-l-amber-500';
    return 'bg-emerald-50 border-l-4 border-l-emerald-500';
  }
  if (partnerType === 'imprimeur') {
    const orderDate = order.order_date ? new Date(order.order_date) : null;
    if (!orderDate) return '';
    const diffDays = Math.floor((now - orderDate) / (1000 * 60 * 60 * 24));
    if (diffDays >= 5) return 'bg-red-50 border-l-4 border-l-red-500';
    if (diffDays >= 3) return 'bg-amber-50 border-l-4 border-l-amber-500';
    return 'bg-emerald-50 border-l-4 border-l-emerald-500';
  }
  return '';
};

export const PartnerDashboard = () => {
  const { user, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weightInputs, setWeightInputs] = useState({});
  const [addressOrder, setAddressOrder] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [imagePopupOpen, setImagePopupOpen] = useState(false);
  const [imagePopupImages, setImagePopupImages] = useState([]);
  const [notifBanner, setNotifBanner] = useState(null);
  const [alertNotifs, setAlertNotifs] = useState([]);
  const prevOrderCount = useRef(null);
  const prevColorKeys = useRef(null);

  useEffect(() => {
    if (user?.language && user.language !== language) {
      setLanguage(user.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  const playNotifSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
      setTimeout(() => {
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.frequency.value = 1000;
        osc2.type = 'sine';
        gain2.gain.value = 0.3;
        osc2.start();
        osc2.stop(ctx.currentTime + 0.3);
      }, 200);
    } catch (e) { /* audio not available */ }
  }, []);

  const fetchOrders = useCallback(async (isPolling = false) => {
    try {
      const [ordersRes, archivesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partner/orders`, { withCredentials: true }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/partner/archives`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      const newOrders = ordersRes.data;

      // Notification: detect new orders
      if (isPolling && prevOrderCount.current !== null && newOrders.length > prevOrderCount.current) {
        const diff = newOrders.length - prevOrderCount.current;
        const msg = diff === 1 ? t('new_order_notification') : `${diff} ${t('new_orders_notification')}`;
        setNotifBanner(msg);
        playNotifSound();
        // Auto-dismiss after 8 seconds
        setTimeout(() => setNotifBanner(null), 8000);
      }
      prevOrderCount.current = newOrders.length;

      // Detect orange/red alerts
      if (isPolling) {
        const pt = user?.partner_type;
        const getColor = (order) => {
          const now = new Date();
          if (pt === 'couture') {
            const bd = order.bureau_delivery_date ? new Date(order.bureau_delivery_date) : null;
            if (!bd) return 'green';
            const diff = Math.floor((now - bd) / 86400000);
            return diff > 3 ? 'red' : diff >= 1 ? 'orange' : 'green';
          }
          if (pt === 'imprimeur') {
            const od = order.order_date ? new Date(order.order_date) : null;
            if (!od) return 'green';
            const diff = Math.floor((now - od) / 86400000);
            return diff >= 5 ? 'red' : diff >= 3 ? 'orange' : 'green';
          }
          return 'green';
        };
        const currentKeys = new Set(newOrders.filter(o => ['orange', 'red'].includes(getColor(o))).map(o => `${o.id}-${getColor(o)}`));
        const prev = prevColorKeys.current || new Set();
        const newColorAlerts = [...currentKeys].filter(k => !prev.has(k));
        if (newColorAlerts.length > 0) {
          const hasRed = newColorAlerts.some(k => k.endsWith('-red'));
          const msg = hasRed ? t('alert_red_notification') : t('alert_orange_notification');
          setAlertNotifs(prev => [...prev, { id: Date.now(), msg }]);
          setTimeout(() => setAlertNotifs([]), 8000);
        }
        prevColorKeys.current = currentKeys;
      }

      setOrders(newOrders);
      setArchives(archivesRes.data);
      const inputs = {};
      newOrders.forEach(o => { inputs[o.id] = o.package_weight_kg || ''; });
      setWeightInputs(inputs);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchOrders(false);
    const interval = setInterval(() => fetchOrders(true), 300000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const groupByMonth = (list) => {
    const groups = {};
    for (const o of list) {
      const label = o.month_label || 'Inconnu';
      if (!groups[label]) groups[label] = [];
      groups[label].push(o);
    }
    return Object.entries(groups).map(([label, items]) => ({ label, orders: items }));
  };

  const toggleMonth = (label) => {
    setExpandedMonths(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner/orders/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      toast.success(`${t('status')}: ${translateStatus(newStatus, t)}`);
      fetchOrders(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleWeightUpdate = async (orderId) => {
    const weight = parseFloat(weightInputs[orderId]);
    if (isNaN(weight) || weight < 0) { toast.error('Invalid'); return; }
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/partner/orders/${orderId}/weight`,
        { package_weight_kg: weight },
        { withCredentials: true }
      );
      toast.success(`${t('weight_label')}: ${weight} kg`);
      fetchOrders(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleLanguageChange = async (lang) => {
    setLanguage(lang);
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/auth/language`, { language: lang }, { withCredentials: true });
    } catch (e) { console.error(e); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const allowedStatuses = user?.allowed_statuses || [];
  const nextStatus = allowedStatuses.length > 1 ? allowedStatuses[1] : null;
  const partnerType = user?.partner_type;
  const isTransport = partnerType === 'transport';
  const isCouture = partnerType === 'couture';
  const isImprimeur = partnerType === 'imprimeur';
  const showWeight = isTransport;
  const showLivraisonPrevue = isTransport;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 relative">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Panther" className="h-10 object-contain" data-testid="partner-logo" />
            <div>
              <p className="text-sm text-slate-600">{user?.name} - {translatePartnerType(partnerType, t)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select value={language} onValueChange={handleLanguageChange}>
              <SelectTrigger data-testid="partner-lang-switcher" className="w-28 h-8 text-xs border-slate-200">
                <Globe size={14} className="mr-1" />
                <SelectValue>{LANG_LABELS[language]}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="vi">Tiếng Việt</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="partner-logout-button" onClick={handleLogout} variant="ghost" className="text-slate-600">
              <LogOut size={18} className="mr-2" />
              {t('logout')}
            </Button>
          </div>
        </div>

        {/* Notification banner */}
        {notifBanner && (
          <div data-testid="new-order-notification" className="absolute top-full left-0 right-0 z-50 animate-in slide-in-from-top">
            <div className="max-w-7xl mx-auto px-6 py-3">
              <div className="bg-emerald-600 text-white rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg">
                <Bell size={20} className="animate-bounce" />
                <span className="font-semibold">{notifBanner}</span>
                <button onClick={() => setNotifBanner(null)} className="ml-auto text-emerald-200 hover:text-white text-lg font-bold">&times;</button>
              </div>
            </div>
          </div>
        )}
        {/* Orange/Red alert notifications */}
        {alertNotifs.length > 0 && (
          <div data-testid="color-alert-notification" className="absolute top-full left-0 right-0 z-50" style={{marginTop: notifBanner ? '60px' : '0'}}>
            <div className="max-w-7xl mx-auto px-6 py-2 space-y-2">
              {alertNotifs.map(n => (
                <div key={n.id} className={`rounded-lg px-4 py-3 flex items-center gap-3 shadow-lg ${n.msg.includes(t('alert_red_notification')) ? 'bg-red-600 text-white' : 'bg-amber-500 text-white'}`}>
                  <Bell size={20} className="animate-bounce" />
                  <span className="font-semibold">{n.msg}</span>
                  <button onClick={() => setAlertNotifs(prev => prev.filter(x => x.id !== n.id))} className="ml-auto opacity-70 hover:opacity-100 text-lg font-bold">&times;</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('partner_orders')}</h2>
          <p className="text-sm text-slate-600 mt-1">
            {translateStatus(allowedStatuses[0] || '', t)} - {t('partner_orders_subtitle')}
          </p>
          <StatusFlowBar />
        </div>

        <Card data-testid="partner-orders-card" className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-medium">
              {orders.length} {t('pending_orders')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : orders.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">{t('no_pending_orders')}</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-medium w-14"></TableHead>
                      <TableHead className="font-medium">{t('order_number')}</TableHead>
                      <TableHead className="font-medium">{t('client')}</TableHead>
                      <TableHead className="font-medium">{t('country')}</TableHead>
                      {isTransport && <TableHead className="font-medium">{t('address')}</TableHead>}
                      {isTransport && <TableHead className="font-medium">{t('pieces')}</TableHead>}
                      {(isCouture || isImprimeur) && <TableHead className="font-medium">{t('order_date')}</TableHead>}
                      {(isCouture || isImprimeur) && <TableHead className="font-medium">{t('delivery_office')}</TableHead>}
                      {showLivraisonPrevue && <TableHead className="font-medium">{t('delivery_expected')}</TableHead>}
                      <TableHead className="font-medium">{t('urgency')}</TableHead>
                      {showWeight && <TableHead className="font-medium">{t('weight_label')}</TableHead>}
                      <TableHead className="font-medium">{t('status')}</TableHead>
                      {(isCouture || isImprimeur) && <TableHead className="font-medium">DROPBOX</TableHead>}
                      {nextStatus && <TableHead className="font-medium">{t('actions')}</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow
                        key={order.id}
                        data-testid="partner-order-row"
                        className={`hover:bg-slate-100 transition-colors ${getRowColor(order, partnerType)}`}
                      >
                        <TableCell>
                          {order.images?.length > 0 ? (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${order.images[0]}`}
                              alt="" data-testid="order-thumbnail"
                              className="w-10 h-10 object-cover rounded border border-slate-300 cursor-pointer hover:opacity-80"
                              onClick={() => { setImagePopupImages(order.images); setImagePopupOpen(true); }}
                            />
                          ) : (
                            <div className="w-10 h-10 bg-slate-200 rounded border border-slate-300 flex items-center justify-center">
                              <ImageIcon size={14} className="text-slate-400" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">{order.order_number}</TableCell>
                        <TableCell>{order.client_name}{order.client_firstname ? ` ${order.client_firstname}` : ''}</TableCell>
                        <TableCell>{order.client_country}</TableCell>
                        {isTransport && (
                          <TableCell>
                            <Button data-testid="partner-address-button" size="sm" variant="outline" className="text-xs uppercase"
                              onClick={() => { setAddressOrder(order); setAddressDialogOpen(true); }}>
                              <MapPin size={14} className="mr-1" />
                              {t('address')}
                            </Button>
                          </TableCell>
                        )}
                        {isTransport && <TableCell>{order.num_pieces} {order.piece_type}</TableCell>}
                        {(isCouture || isImprimeur) && (
                          <TableCell className="text-sm">{new Date(order.order_date).toLocaleDateString('fr-FR')}</TableCell>
                        )}
                        {(isCouture || isImprimeur) && (
                          <TableCell className="text-sm font-medium">
                            {order.bureau_delivery_date ? new Date(order.bureau_delivery_date).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                        )}
                        {showLivraisonPrevue && (
                          <TableCell className="text-sm">{new Date(order.delivery_date).toLocaleDateString('fr-FR')}</TableCell>
                        )}
                        <TableCell>
                          <Badge className={`border text-xs uppercase ${
                            order.urgency === 'urgent' ? 'bg-red-100 text-red-800 border-red-200' :
                            order.urgency === 'prioritaire' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                            order.urgency === 'classique' ? 'bg-slate-100 text-slate-800 border-slate-200' :
                            'bg-green-100 text-green-800 border-green-200'
                          }`}>
                            {translateUrgency(order.urgency, t)}
                          </Badge>
                        </TableCell>
                        {showWeight && (
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Input data-testid="partner-weight-input" type="number" step="0.1" min="0"
                                value={weightInputs[order.id] || ''}
                                onChange={(e) => setWeightInputs({ ...weightInputs, [order.id]: e.target.value })}
                                className="w-20 h-8 text-sm" />
                              <Button data-testid="partner-save-weight-button" size="sm" variant="outline" className="h-8 px-2"
                                onClick={() => handleWeightUpdate(order.id)}>
                                <Package size={14} />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800 border border-blue-200 text-xs uppercase">
                            {translateStatus(order.status, t)}
                          </Badge>
                        </TableCell>
                        {(isCouture || isImprimeur) && (
                          <TableCell>
                            {order.dropbox_link ? (
                              <a href={order.dropbox_link} target="_blank" rel="noreferrer">
                                <Button data-testid="partner-dropbox-button" size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase">DROPBOX</Button>
                              </a>
                            ) : <span className="text-xs text-slate-400">-</span>}
                          </TableCell>
                        )}
                        {nextStatus && (
                          <TableCell>
                            <Button data-testid="partner-change-status-button" size="sm" className="bg-slate-900 hover:bg-slate-800 text-xs uppercase"
                              onClick={() => handleStatusChange(order.id, nextStatus)}>
                              <ArrowRight size={14} className="mr-1" />
                              {translateStatus(nextStatus, t)}
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {isTransport && (
          <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('address_delivery')} - {addressOrder?.order_number}</DialogTitle>
              </DialogHeader>
              {addressOrder && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-xs text-slate-500">{t('name')}</Label>
                      <p className="font-medium text-lg">{addressOrder.client_name}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{t('firstname')}</Label>
                      <p className="font-medium text-lg">{addressOrder.client_firstname || '-'}</p>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">{t('address')}</Label>
                    <p className="font-medium">{addressOrder.client_address || t('not_provided')}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-xs text-slate-500">{t('country')}</Label>
                      <p className="font-medium">{addressOrder.client_country}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">{t('phone')}</Label>
                      <p className="font-medium">{addressOrder.client_phone || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        )}

        {/* ARCHIVES SECTION for Imprimeur and Couture */}
        {(isCouture || isImprimeur) && archives.length > 0 && (
          <Card data-testid="partner-archives-card" className="border-slate-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Archive size={20} className="text-slate-600" />
                <CardTitle className="text-lg font-medium">{t('partner_archives')}</CardTitle>
              </div>
              <p className="text-xs text-slate-500 mt-1">{archives.length} {t('partner_archives_subtitle')}</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {groupByMonth(archives).map((group) => (
                  <div key={group.label} data-testid="partner-archive-month-group" className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      data-testid="toggle-partner-archive-month"
                      className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
                      onClick={() => toggleMonth(group.label)}
                    >
                      <div className="flex items-center gap-3">
                        {expandedMonths[group.label] ? <ChevronDown size={18} className="text-slate-500" /> : <ChevronRight size={18} className="text-slate-500" />}
                        <span className="font-semibold text-slate-900">{group.label}</span>
                      </div>
                      <Badge className="bg-slate-200 text-slate-700 border-0">{group.orders.length}</Badge>
                    </button>
                    {expandedMonths[group.label] && (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-white">
                              <TableHead className="font-medium w-14"></TableHead>
                              <TableHead className="font-medium">{t('order_number')}</TableHead>
                              <TableHead className="font-medium">{t('client')}</TableHead>
                              <TableHead className="font-medium">{t('country')}</TableHead>
                              <TableHead className="font-medium">{t('order_date')}</TableHead>
                              <TableHead className="font-medium">{t('delivery_office')}</TableHead>
                              <TableHead className="font-medium">{t('status')}</TableHead>
                              <TableHead className="font-medium">DROPBOX</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {group.orders.map((order) => (
                              <TableRow key={order.id} data-testid="partner-archive-row" className="hover:bg-slate-50">
                                <TableCell>
                                  {order.images?.length > 0 ? (
                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${order.images[0]}`} alt=""
                                      className="w-10 h-10 object-cover rounded border cursor-pointer hover:opacity-80"
                                      onClick={() => { setImagePopupImages(order.images); setImagePopupOpen(true); }} />
                                  ) : (
                                    <div className="w-10 h-10 bg-slate-200 rounded flex items-center justify-center">
                                      <ImageIcon size={14} className="text-slate-400" />
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">{order.order_number}</TableCell>
                                <TableCell>{order.client_name} {order.client_firstname}</TableCell>
                                <TableCell>{order.client_country}</TableCell>
                                <TableCell className="text-sm">{new Date(order.order_date).toLocaleDateString('fr-FR')}</TableCell>
                                <TableCell className="text-sm font-medium">{order.bureau_delivery_date ? new Date(order.bureau_delivery_date).toLocaleDateString('fr-FR') : '-'}</TableCell>
                                <TableCell>
                                  <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs uppercase">{translateStatus(order.status, t)}</Badge>
                                </TableCell>
                                <TableCell>
                                  {order.dropbox_link ? (
                                    <a href={order.dropbox_link} target="_blank" rel="noreferrer">
                                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs uppercase">DROPBOX</Button>
                                    </a>
                                  ) : <span className="text-xs text-slate-400">-</span>}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={imagePopupOpen} onOpenChange={setImagePopupOpen}>
          <DialogContent className="max-w-2xl" data-testid="image-popup-dialog">
            <DialogHeader>
              <DialogTitle>{t('image_popup_title')}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              {imagePopupImages.map((img, i) => (
                <img key={i} src={`${process.env.REACT_APP_BACKEND_URL}${img}`} alt="" className="w-full rounded-lg border border-slate-200" />
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};
